import { CircleHelp, Copy } from "lucide-react";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";

import { Card } from "flowbite-react";

import { NormalParagraph, Heading4 } from "../../lib/ui/components/typography/Typography";
import Button from "../../lib/ui/components/button/Button";
import Tooltip from "../../lib/ui/components/tooltip/Tooltip";
import ToggleField from "../../lib/ui/components/toggle-field/ToggleField";
import Checkbox from "../../lib/ui/components/checkbox/Checkbox";
import Input from "../../lib/ui/components/input/Input";
import Textarea from "../../lib/ui/components/input/Textarea";
import { SAVE_INTRO_MESSAGE, SAVE_OUTRO_MESSAGE, SAVE_CALENDAR_MESSAGE, SAVE_CALENDAR_REMINDER_MESSAGE, GET_JOB_MESSAGES, SAVE_CALENDAR_SETTINGS, GET_CALENDAR_SETTINGS, UPDATE_JOB_INTERVIEW_TYPES, REPLACE_SNAPSHOT_DESIRED_RESPONSES, REGENERATE_SCORING_RUBRICS, DIGEST_ROLE, DIGEST_ROLES, DIGEST_SETTINGS } from "../../lib/urls";
import { parseInterviewTypes, ensureInterviewTypesArray, isVoiceFeatureEnabled, isInterviewTypePickerEnabled } from "../../utils/interviewTypes";
import { isDevEnvironment } from "../../utils/envUtils";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import Modal from "../../lib/ui/components/Modal";
import JobAtsStageSettings from "./JobAtsStageSettings";
import JobSettingsSectionNav from "./JobSettingsSectionNav";
import {
    ScheduleEditor,
    roleScheduleLabel,
    DIGEST_SCHEDULE_FORM_ID,
} from "../Settings/ShortlistDigestCard";

const SETTINGS_SECTION = {
    interviewTypes: "job-settings-interview-types",
    calendar: "job-settings-calendar",
    notifications: "job-settings-notifications",
    messages: "job-settings-messages",
    atsStages: "job-settings-ats-stages",
};

function JobDigestRoleControl({ jobId, token }) {
    const [role, setRole] = useState(null);
    const [digestPrefs, setDigestPrefs] = useState(null);
    const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async ({ silent = false } = {}) => {
        if (!token || !jobId) return;
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const [rolesRes, settingsRes] = await Promise.all([
                fetch(DIGEST_ROLES, { headers }),
                fetch(DIGEST_SETTINGS, { headers }),
            ]);
            if (settingsRes.ok) {
                setDigestPrefs(await settingsRes.json());
            }
            if (!rolesRes.ok) return;
            const data = await rolesRes.json();
            const match = (data.roles || []).find((r) => r.job_id === jobId);
            setRole(match || null);
        } catch (e) {
            if (!silent) console.error(e);
        }
    }, [token, jobId]);

    useEffect(() => {
        load();
    }, [load]);

    const update = async (body) => {
        const previousRole = role;
        if (body.subscribed !== undefined && role) {
            setRole({ ...role, subscribed: body.subscribed });
        }
        setSaving(true);
        try {
            const res = await fetch(DIGEST_ROLE(jobId), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(body),
            });
            if (!res.ok) throw new Error("failed");
            toast.success("Digest settings updated");
            setScheduleModalOpen(false);
            await load({ silent: true });
        } catch (e) {
            setRole(previousRole);
            toast.error("Failed to update digest settings");
        } finally {
            setSaving(false);
        }
    };

    if (!role || !digestPrefs) return null;

    const companyDisabled = digestPrefs.company_digest_enabled === false;
    const masterOff = !digestPrefs.digest_enabled;
    const toggleDisabled = saving || companyDisabled || masterOff;
    const subscribedChecked =
        companyDisabled || masterOff ? false : !!role.subscribed;
    const scheduleLabel = roleScheduleLabel(role.schedule);
    const scheduleLinkEnabled = subscribedChecked && !toggleDisabled;

    const scheduleControl = scheduleLinkEnabled ? (
        <button
            type="button"
            className="underline text-primary-main"
            onClick={() => setScheduleModalOpen(true)}
        >
            {scheduleLabel}
        </button>
    ) : (
        <span className="text-gray-400">{scheduleLabel}</span>
    );

    return (
        <div className="pt-2">
            {companyDisabled && (
                <p className="text-body text-amber-700 mb-3">
                    Digests are turned off for your company by an admin.
                </p>
            )}
            <ToggleField
                label="My digest for this role"
                description={<>Only affects you. {scheduleControl}</>}
                checked={subscribedChecked}
                disabled={toggleDisabled}
                disabledTooltip={
                    companyDisabled
                        ? "Contact your company admin if you want shortlist digests turned on."
                        : undefined
                }
                onChange={(v) => {
                    if (!v) setScheduleModalOpen(false);
                    update({ subscribed: v });
                }}
            />
            <Modal
                show={scheduleModalOpen}
                onClose={() => !saving && setScheduleModalOpen(false)}
                size="md"
                placement="center"
                dismissible={!saving}
            >
                <Modal.Header className="!border-b border-gray-200">
                    <Heading4 className="!mb-0">Schedule for this role</Heading4>
                </Modal.Header>
                <Modal.Body className="pt-4">
                    <ScheduleEditor
                        inModal
                        saving={saving}
                        schedule={role.schedule}
                        onCancel={() => setScheduleModalOpen(false)}
                        onReset={() => update({ reset_schedule: true })}
                        onSave={(patch) => update(patch)}
                    />
                </Modal.Body>
                <Modal.Footer className="!border-t border-gray-200 flex flex-wrap items-center justify-between gap-3">
                    <Button
                        variant="ghost"
                        disabled={saving}
                        onClick={() => setScheduleModalOpen(false)}
                    >
                        Cancel
                    </Button>
                    <div className="flex flex-wrap items-center justify-end gap-3 ml-auto">
                        {role.schedule?.is_custom && (
                            <Button
                                variant="secondary"
                                disabled={saving}
                                onClick={() => update({ reset_schedule: true })}
                            >
                                Reset to Default
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            type="submit"
                            form={DIGEST_SCHEDULE_FORM_ID}
                            disabled={saving}
                        >
                            Save
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default function JobSettings({
    jobId,
    companyId,
    token,
    introMessage: initialIntroMessage = "",
    outroMessage: initialOutroMessage = "",
    defaultIntroMessage = "",
    defaultOutroMessage = "",
    calendarMessage: initialCalendarMessage = "",
    calendarReminderMessage: initialCalendarReminderMessage = "",
    defaultCalendarMessage = "",
    defaultCalendarReminderMessage = "",
    onMessageSaved,
    calendarLink: initialCalendarLink = "",
    calendarMatchRating: initialCalendarMatchRating = 4,
    automaticScheduling = false,
    emailNotificationEnabled = true,
    onCalendarSettingsSaved,
    onAutomaticSchedulingChange,
    onNotificationChange,
    allowedInterviewTypes: initialAllowedInterviewTypes = ["TEXT"],
    onInterviewTypesSaved,
    atsJobId = "",
    automaticInterviews = false,
    interviewEligibleStageNames,
    autoAdvanceTargetStage,
    autoAdvanceHighMatchStage,
    autoAdvanceMinMatchRating,
    autoAdvanceHumanInterviewStage,
    onStageSettingsSaved,
    onAutomaticInterviewsChange,
    onScoringRubricsRegenerated,
}) {
    const [introMessage, setIntroMessage] = useState(initialIntroMessage);
    const [outroMessage, setOutroMessage] = useState(initialOutroMessage);
    const [introMessageChanged, setIntroMessageChanged] = useState(false);
    const [outroMessageChanged, setOutroMessageChanged] = useState(false);
    const [calendarMessage, setCalendarMessage] = useState(initialCalendarMessage);
    const [calendarReminderMessage, setCalendarReminderMessage] = useState(initialCalendarReminderMessage);
    const [calendarMessageChanged, setCalendarMessageChanged] = useState(false);
    const [calendarReminderMessageChanged, setCalendarReminderMessageChanged] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    // Calendar Settings state
    const [localAutomaticScheduling, setLocalAutomaticScheduling] = useState(automaticScheduling);
    const [isToggling, setIsToggling] = useState(false);
    const [calendarLink, setCalendarLink] = useState(initialCalendarLink);
    const [matchRating, setMatchRating] = useState(initialCalendarMatchRating);
    const [calendarSettingsChanged, setCalendarSettingsChanged] = useState(false);
    const [calendarLinkError, setCalendarLinkError] = useState("");

    // Notification Settings state
    const [localEmailNotificationEnabled, setLocalEmailNotificationEnabled] = useState(emailNotificationEnabled);
    const [isNotificationToggling, setIsNotificationToggling] = useState(false);

    // Interview Types state
    const [localAllowedInterviewTypes, setLocalAllowedInterviewTypes] = useState(
        ensureInterviewTypesArray(initialAllowedInterviewTypes)
    );
    const [interviewTypesChanged, setInterviewTypesChanged] = useState(false);
    const [showSnapshotMigrationModal, setShowSnapshotMigrationModal] = useState(false);
    const [isMigratingSnapshots, setIsMigratingSnapshots] = useState(false);
    const [showRubricRegenerationModal, setShowRubricRegenerationModal] = useState(false);
    const [isRegeneratingRubrics, setIsRegeneratingRubrics] = useState(false);

    // Sync local automatic scheduling state with prop changes
    useEffect(() => {
        setLocalAutomaticScheduling(automaticScheduling);
    }, [automaticScheduling]);

    // Sync local email notification state with prop changes
    useEffect(() => {
        setLocalEmailNotificationEnabled(emailNotificationEnabled);
    }, [emailNotificationEnabled]);

    // Sync calendar link and match rating with prop changes
    useEffect(() => {
        setCalendarLink(initialCalendarLink);
        setMatchRating(initialCalendarMatchRating);
    }, [initialCalendarLink, initialCalendarMatchRating]);

    // Sync interview types with prop changes
    useEffect(() => {
        setLocalAllowedInterviewTypes(ensureInterviewTypesArray(initialAllowedInterviewTypes));
    }, [initialAllowedInterviewTypes]);

    // Check if interview types have changed
    useEffect(() => {
        const current = ensureInterviewTypesArray(localAllowedInterviewTypes, []);
        const initial = ensureInterviewTypesArray(initialAllowedInterviewTypes, []);
        const hasChanged = JSON.stringify([...current].sort()) !== JSON.stringify([...initial].sort());
        setInterviewTypesChanged(hasChanged);
    }, [localAllowedInterviewTypes, initialAllowedInterviewTypes]);

    // Sync calendar message and reminder message with prop changes
    useEffect(() => {
        setCalendarMessage(initialCalendarMessage);
        setCalendarReminderMessage(initialCalendarReminderMessage);
    }, [initialCalendarMessage, initialCalendarReminderMessage]);

    // Check if messages have changed from initial values
    useEffect(() => {
        setIntroMessageChanged(introMessage !== initialIntroMessage);
        setOutroMessageChanged(outroMessage !== initialOutroMessage);
    }, [introMessage, outroMessage, initialIntroMessage, initialOutroMessage]);

    // Check if calendar messages have changed from initial values
    useEffect(() => {
        setCalendarMessageChanged(calendarMessage !== initialCalendarMessage);
        setCalendarReminderMessageChanged(calendarReminderMessage !== initialCalendarReminderMessage);
    }, [calendarMessage, calendarReminderMessage, initialCalendarMessage, initialCalendarReminderMessage]);

    // Mirrors the suffix appended in sms_service._get_welcome_message so the
    // recruiter preview matches what candidates actually receive.
    const autoAppendedInviteText = () => {
        const types = ensureInterviewTypesArray(localAllowedInterviewTypes, []);
        const hasText = types.includes("TEXT");
        const hasVoice = types.includes("VOICE") && isVoiceFeatureEnabled();

        if (hasText && hasVoice) {
            return "Reply YES to get started, or NO and I'll close this out. You can also call your interview number to start a voice interview.";
        }
        if (hasVoice) {
            return "Please call your interview number to start your voice interview, or reply NO and I'll close this out.";
        }
        return "Reply YES to get started, or NO and I'll close this out.";
    };

    const saveIntroMessageHandler = async () => {
        if (!introMessageChanged) return;

        setIsLoading(true);
        try {
            const response = await fetch(SAVE_INTRO_MESSAGE(companyId, jobId), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    intro_message: introMessage,
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to save intro message");
            }

            toast.success("Intro message saved successfully!");
            setIntroMessageChanged(false);
            // Notify parent component that message was saved
            if (onMessageSaved) {
                onMessageSaved('intro', introMessage);
            }
        } catch (error) {
            console.error("Error saving intro message:", error);
            toast.error("Failed to save intro message");
        } finally {
            setIsLoading(false);
        }
    };

    const saveOutroMessageHandler = async () => {
        if (!outroMessageChanged) return;

        setIsLoading(true);
        try {
            const response = await fetch(SAVE_OUTRO_MESSAGE(companyId, jobId), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    outro_message: outroMessage,
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to save outro message");
            }

            toast.success("Outro message saved successfully!");
            setOutroMessageChanged(false);
            // Notify parent component that message was saved
            if (onMessageSaved) {
                onMessageSaved('outro', outroMessage);
            }
        } catch (error) {
            console.error("Error saving outro message:", error);
            toast.error("Failed to save outro message");
        } finally {
            setIsLoading(false);
        }
    };

    const resetIntroMessageHandler = () => {
        setIntroMessage(initialIntroMessage);
        setIntroMessageChanged(false);
    };

    const resetOutroMessageHandler = () => {
        setOutroMessage(initialOutroMessage);
        setOutroMessageChanged(false);
    };

    const resetIntroToDefaultHandler = () => {
        setIntroMessage(defaultIntroMessage);
        setIntroMessageChanged(true);
    };

    const resetOutroToDefaultHandler = () => {
        setOutroMessage(defaultOutroMessage);
        setOutroMessageChanged(true);
    };

    const saveCalendarMessageHandler = async () => {
        if (!calendarMessageChanged) return;

        setIsLoading(true);
        try {
            const response = await fetch(SAVE_CALENDAR_MESSAGE(companyId, jobId), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    calendar_message: calendarMessage,
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to save calendar message");
            }

            toast.success("Calendar message saved successfully!");
            setCalendarMessageChanged(false);
            // Notify parent component that message was saved
            if (onMessageSaved) {
                onMessageSaved('calendar', calendarMessage);
            }
        } catch (error) {
            console.error("Error saving calendar message:", error);
            toast.error("Failed to save calendar message");
        } finally {
            setIsLoading(false);
        }
    };

    const saveCalendarReminderMessageHandler = async () => {
        if (!calendarReminderMessageChanged) return;

        setIsLoading(true);
        try {
            const response = await fetch(SAVE_CALENDAR_REMINDER_MESSAGE(companyId, jobId), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    calendar_reminder_message: calendarReminderMessage,
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to save calendar reminder message");
            }

            toast.success("Calendar reminder message saved successfully!");
            setCalendarReminderMessageChanged(false);
            // Notify parent component that message was saved
            if (onMessageSaved) {
                onMessageSaved('calendarReminder', calendarReminderMessage);
            }
        } catch (error) {
            console.error("Error saving calendar reminder message:", error);
            toast.error("Failed to save calendar reminder message");
        } finally {
            setIsLoading(false);
        }
    };

    const resetCalendarMessageHandler = () => {
        setCalendarMessage(initialCalendarMessage);
        setCalendarMessageChanged(false);
    };

    const resetCalendarReminderMessageHandler = () => {
        setCalendarReminderMessage(initialCalendarReminderMessage);
        setCalendarReminderMessageChanged(false);
    };

    const resetCalendarMessageToDefaultHandler = () => {
        setCalendarMessage(defaultCalendarMessage);
        setCalendarMessageChanged(true);
    };

    const resetCalendarReminderMessageToDefaultHandler = () => {
        setCalendarReminderMessage(defaultCalendarReminderMessage);
        setCalendarReminderMessageChanged(true);
    };

    const copyVariable = (variable) => {
        navigator.clipboard.writeText(variable);
        toast.success(`${variable} copied!`);
    };

    const handleInterviewTypeChange = (type, checked) => {
        const currentTypes = ensureInterviewTypesArray(localAllowedInterviewTypes, []);
        let newTypes;
        if (checked) {
            newTypes = [...currentTypes, type];
        } else {
            newTypes = currentTypes.filter(t => t !== type);
        }
        setLocalAllowedInterviewTypes(newTypes);
    };

    const resetInterviewTypesHandler = () => {
        setLocalAllowedInterviewTypes(initialAllowedInterviewTypes || ["TEXT"]);
        setInterviewTypesChanged(false);
    };

    const saveInterviewTypesHandler = async () => {
        const types = ensureInterviewTypesArray(localAllowedInterviewTypes, []);
        if (!interviewTypesChanged || types.length === 0) return;

        setIsLoading(true);
        try {
            const response = await fetch(
                UPDATE_JOB_INTERVIEW_TYPES(companyId, jobId),
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        allowed_interview_types: ensureInterviewTypesArray(localAllowedInterviewTypes, []),
                    }),
                }
            );

            if (!response.ok) {
                throw new Error("Failed to save interview types");
            }

            toast.success("Interview types saved successfully!");
            setInterviewTypesChanged(false);
            
            if (onInterviewTypesSaved) {
                onInterviewTypesSaved(ensureInterviewTypesArray(localAllowedInterviewTypes, []));
            }
        } catch (error) {
            console.error("Error saving interview types:", error);
            toast.error("Failed to save interview types");
        } finally {
            setIsLoading(false);
        }
    };

    // URL validation function
    const validateURL = (url) => {
        // Empty URL is not valid
        if (!url || url.trim() === "") {
            return { isValid: false, error: "Calendar link is required" };
        }

        try {
            // Use URL constructor for validation
            const urlObj = new URL(url);
            
            // Check if protocol is http or https
            if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
                return { 
                    isValid: false, 
                    error: "URL must start with http:// or https://" 
                };
            }
            
            return { isValid: true, error: "" };
        } catch (error) {
            return { 
                isValid: false, 
                error: "Please enter a valid URL (e.g., https://example.com)" 
            };
        }
    };

    // Calendar Settings handlers
    const handleCalendarLinkChange = (e) => {
        const newValue = e.target.value;
        setCalendarLink(newValue);
        
        // Validate URL only if not empty (to avoid showing "required" error while typing)
        if (newValue.trim() !== "") {
            const validation = validateURL(newValue);
            setCalendarLinkError(validation.error);
            
            if (validation.isValid) {
                setCalendarSettingsChanged(true);
            }
        } else {
            // Clear error when empty (but Save button will still be disabled)
            setCalendarLinkError("");
            setCalendarSettingsChanged(true);
        }
    };

    const handleMatchRatingChange = (e) => {
        setMatchRating(parseInt(e.target.value));
        setCalendarSettingsChanged(true);
    };

    const resetCalendarSettingsHandler = () => {
        setCalendarLink(initialCalendarLink);
        setMatchRating(initialCalendarMatchRating);
        setCalendarSettingsChanged(false);
        setCalendarLinkError(""); // Clear any validation errors
    };

    const saveCalendarSettingsHandler = async () => {
        if (!calendarSettingsChanged) return;

        // Validate URL before saving
        const validation = validateURL(calendarLink);
        if (!validation.isValid) {
            setCalendarLinkError(validation.error);
            toast.error("Please fix the calendar link error before saving");
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(SAVE_CALENDAR_SETTINGS(companyId, jobId), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    automatic_scheduling: localAutomaticScheduling,
                    calendar_link: calendarLink,
                    calendar_match_rating: matchRating,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to save calendar settings");
            }

            toast.success("Calendar settings saved successfully!");
            setCalendarSettingsChanged(false);
            setCalendarLinkError(""); // Clear any errors on success
            
            // Notify parent component that calendar settings were saved
            if (onCalendarSettingsSaved) {
                onCalendarSettingsSaved(calendarLink, matchRating);
            }
            
            // If automatic scheduling is ON, notify parent component to update state
            if (localAutomaticScheduling && onAutomaticSchedulingChange) {
                onAutomaticSchedulingChange(true);
            }
        } catch (error) {
            console.error("Error saving calendar settings:", error);
            toast.error(error.message || "Failed to save calendar settings");
        } finally {
            setIsLoading(false);
        }
    };

    const replaceSnapshotDesiredResponses = async () => {
        setIsMigratingSnapshots(true);
        try {
            const response = await fetch(
                REPLACE_SNAPSHOT_DESIRED_RESPONSES(companyId, jobId),
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || "Failed to update interview snapshots");
            }

            const updated = data.snapshots_updated || 0;
            const criteriaUpdated = data.analysis_criteria_updated || 0;
            const skipped = (data.unmatched || 0) + (data.missing_current_rubric || 0);
            const criteriaMessage = criteriaUpdated
                ? ` Refreshed the evaluation criteria on ${criteriaUpdated} interview${criteriaUpdated === 1 ? "" : "s"}.`
                : "";
            const skippedMessage = skipped
                ? ` ${skipped} could not be safely matched to a current rubric and were left unchanged.`
                : "";
            toast.success(
                `Updated ${updated} snapshot record${updated === 1 ? "" : "s"} across ${data.sessions_updated || 0} interview${data.sessions_updated === 1 ? "" : "s"}.${criteriaMessage}${skippedMessage}`
            );
            setShowSnapshotMigrationModal(false);
        } catch (error) {
            console.error("Error replacing snapshot desired responses:", error);
            toast.error(error.message || "Failed to update interview snapshots");
        } finally {
            setIsMigratingSnapshots(false);
        }
    };

    const regenerateScoringRubrics = async () => {
        setIsRegeneratingRubrics(true);
        try {
            const response = await fetch(REGENERATE_SCORING_RUBRICS(companyId, jobId), {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.detail || "Failed to regenerate Scoring Rubrics");
            }

            onScoringRubricsRegenerated?.(data.interview_questions || []);
            const message = `Regenerated ${data.regenerated} of ${data.questions} Scoring Rubric${data.questions === 1 ? "" : "s"}.`;
            if (data.failed) {
                toast.error(`${message} ${data.failed} failed; run it again to retry them.`);
            } else {
                toast.success(message);
            }
            setShowRubricRegenerationModal(false);
        } catch (error) {
            console.error("Error regenerating Scoring Rubrics:", error);
            toast.error(error.message || "Failed to regenerate Scoring Rubrics");
        } finally {
            setIsRegeneratingRubrics(false);
        }
    };

    const settingsSections = useMemo(() => {
        const sections = [];
        if (isInterviewTypePickerEnabled()) {
            sections.push({
                id: SETTINGS_SECTION.interviewTypes,
                label: "Interview Types",
            });
        }
        sections.push(
            { id: SETTINGS_SECTION.calendar, label: "Calendar Settings" },
            { id: SETTINGS_SECTION.notifications, label: "Notification Settings" },
            { id: SETTINGS_SECTION.messages, label: "Message Settings" },
            { id: SETTINGS_SECTION.atsStages, label: "ATS Stages" },
        );
        return sections;
    }, []);


    // Selecting a section replaces the view. Sections stay mounted (hidden via
    // CSS) so unsaved edits survive switching.
    const [activeSectionId, setActiveSectionId] = useState(null);

    useEffect(() => {
        if (settingsSections.length > 0 && !activeSectionId) {
            setActiveSectionId(settingsSections[0].id);
        }
    }, [settingsSections, activeSectionId]);

    const sectionClass = (sectionId) =>
        `flex w-full flex-col gap-2 ${
            activeSectionId === sectionId ? "" : "hidden"
        }`;

    return (
        <div className="w-full bg-white">
                <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
                    <JobSettingsSectionNav
                        orientation="horizontal"
                        sections={settingsSections}
                        activeSectionId={activeSectionId}
                        onSectionSelect={setActiveSectionId}
                        className="lg:hidden"
                    />
                    <JobSettingsSectionNav
                        sections={settingsSections}
                        activeSectionId={activeSectionId}
                        onSectionSelect={setActiveSectionId}
                        className="hidden lg:sticky lg:top-0 lg:block lg:w-40 lg:shrink-0"
                    />
                    <div className="flex min-w-0 flex-1 flex-col gap-8">
                    {isInterviewTypePickerEnabled() && (
                    <section
                        id={SETTINGS_SECTION.interviewTypes}
                        className={sectionClass(SETTINGS_SECTION.interviewTypes)}
                        aria-labelledby={`${SETTINGS_SECTION.interviewTypes}-heading`}
                    >
                        <div
                            id={`${SETTINGS_SECTION.interviewTypes}-heading`}
                            className="font-semibold text-gray-600 text-body-lg mb-1"
                        >
                            Interview Types
                        </div>

                    <Card className="w-full mb-2">
                        <div className="mb-2 text-body-lg">Interview Types</div>
                        <div className="text-small text-gray-500 mb-4">
                            Select which interview types are allowed for this job.
                        </div>
                        <div className="flex flex-col gap-3 mb-4">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <Checkbox
                                    checked={ensureInterviewTypesArray(localAllowedInterviewTypes, []).includes("TEXT")}
                                    onChange={(e) => handleInterviewTypeChange("TEXT", e.target.checked)}
                                />
                                <div className="flex-1">
                                    <span className="font-medium text-gray-900">Text (SMS)</span>
                                    <div className="text-small text-gray-500">
                                        Candidates respond via text messages
                                    </div>
                                </div>
                            </label>
                            {isVoiceFeatureEnabled() && (
                            <label className="flex items-center gap-3 cursor-pointer">
                                <Checkbox
                                    checked={ensureInterviewTypesArray(localAllowedInterviewTypes, []).includes("VOICE")}
                                    onChange={(e) => handleInterviewTypeChange("VOICE", e.target.checked)}
                                />
                                <div className="flex-1">
                                    <span className="font-medium text-gray-900">Voice</span>
                                    <div className="text-small text-gray-500">
                                        Candidates respond via voice calls
                                    </div>
                                </div>
                            </label>
                            )}
                        </div>
                        {ensureInterviewTypesArray(localAllowedInterviewTypes, []).length === 0 && (
                            <p className="text-small text-red-500 mb-4">
                                Please select at least one interview type
                            </p>
                        )}
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="tertiary"
                                onClick={resetInterviewTypesHandler}
                                disabled={!interviewTypesChanged}
                            >
                                Reset
                            </Button>
                            <Button
                                variant="primary"
                                onClick={saveInterviewTypesHandler}
                                disabled={!interviewTypesChanged || ensureInterviewTypesArray(localAllowedInterviewTypes, []).length === 0 || isLoading}
                            >
                                Save
                            </Button>
                        </div>
                    </Card>
                    </section>
                    )}

                    <section
                        id={SETTINGS_SECTION.calendar}
                        className={sectionClass(SETTINGS_SECTION.calendar)}
                        aria-labelledby={`${SETTINGS_SECTION.calendar}-heading`}
                    >
                        <div
                            id={`${SETTINGS_SECTION.calendar}-heading`}
                            className="font-semibold text-gray-600 text-body-lg mb-1"
                        >
                            Calendar Settings
                        </div>

                    <Card className="w-full mb-2">
                        {/* Automatic Scheduling Toggle */}
                        <div className="mb-6 pb-6 border-b border-gray-200">
                            <ToggleField
                                label="Automatic Scheduling"
                                description="Allow June to automatically send a calendar link to your top applicants."
                                checked={localAutomaticScheduling}
                                disabled={isToggling}
                                onChange={(checked) => {
                                    if (isToggling) return;

                                    setLocalAutomaticScheduling(checked);

                                    if (!checked) {
                                        setIsToggling(true);
                                        if (onAutomaticSchedulingChange) {
                                            Promise.resolve(onAutomaticSchedulingChange(false))
                                                .finally(() => {
                                                    setIsToggling(false);
                                                });
                                        } else {
                                            setIsToggling(false);
                                        }
                                    } else if (calendarLink && calendarLink.trim() !== "") {
                                        setIsToggling(true);
                                        if (onAutomaticSchedulingChange) {
                                            Promise.resolve(onAutomaticSchedulingChange(true))
                                                .finally(() => {
                                                    setIsToggling(false);
                                                });
                                        } else {
                                            setIsToggling(false);
                                        }
                                    } else {
                                        setCalendarSettingsChanged(true);
                                    }
                                }}
                            />
                        </div>

                        <div className="mb-4">
                            <label 
                                className="block text-small font-medium mb-2 text-gray-700"
                                htmlFor="calendar-link-input"
                            >
                                Calendar Link
                            </label>
                            <Input
                                id="calendar-link-input"
                                type="url"
                                error={!!calendarLinkError}
                                value={calendarLink}
                                onChange={handleCalendarLinkChange}
                                placeholder="Enter your calendar link"
                                aria-label="Calendar Link"
                                aria-invalid={calendarLinkError ? "true" : "false"}
                                aria-describedby={calendarLinkError ? "calendar-link-error" : undefined}
                            />
                            {calendarLinkError && (
                                <p id="calendar-link-error" className="mt-1 text-small text-red-600">
                                    {calendarLinkError}
                                </p>
                            )}
                        </div>
                        <div className="mb-4">
                            <label 
                                className={`block text-small font-medium mb-2 ${
                                    !localAutomaticScheduling ? 'text-gray-400' : 'text-gray-700'
                                }`}
                                htmlFor="match-rating-select"
                            >
                                Send Calendar Link to Applicants with a Match Rating of...
                            </label>
                            <select
                                id="match-rating-select"
                                value={matchRating}
                                onChange={handleMatchRatingChange}
                                className={`w-full border rounded-md p-2 ${
                                    !localAutomaticScheduling
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed border-gray-300'
                                        : 'bg-gray-50 text-black border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                                }`}
                                disabled={!localAutomaticScheduling}
                                aria-label="Match Rating Threshold"
                            >
                                <option value={1}>1 and above</option>
                                <option value={2}>2 and above</option>
                                <option value={3}>3 and above</option>
                                <option value={4}>4 and above</option>
                                <option value={5}>5 only</option>
                            </select>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="tertiary"
                                onClick={resetCalendarSettingsHandler}
                                disabled={!calendarSettingsChanged}
                            >
                                Reset
                            </Button>
                            <Tooltip 
                                content={
                                    (calendarLink.trim() && 
                                     !calendarLinkError &&
                                     calendarSettingsChanged)
                                        ? ""  // No tooltip when button is enabled
                                        : !calendarLink.trim() || !!calendarLinkError
                                            ? "Please enter a valid calendar link"
                                            : ""
                                }
                                position="top"
                            >
                                <Button
                                    variant="primary"
                                    onClick={saveCalendarSettingsHandler}
                                    disabled={
                                        !calendarSettingsChanged || 
                                        isLoading || 
                                        !!calendarLinkError || 
                                        !calendarLink.trim()
                                    }
                                >
                                    Save
                                </Button>
                            </Tooltip>
                        </div>
                    </Card>
                    </section>

                    <section
                        id={SETTINGS_SECTION.notifications}
                        className={sectionClass(SETTINGS_SECTION.notifications)}
                        aria-labelledby={`${SETTINGS_SECTION.notifications}-heading`}
                    >
                        <div
                            id={`${SETTINGS_SECTION.notifications}-heading`}
                            className="font-semibold text-gray-600 text-body-lg mb-1"
                        >
                            Notification Settings
                        </div>

                    <Card className="w-full mb-2">
                        <div className="mb-6 pb-6 border-b border-gray-200">
                            <ToggleField
                                label="Email Notifications"
                                description="Send email notifications when candidates complete their interviews for this job."
                                checked={localEmailNotificationEnabled}
                                disabled={isNotificationToggling}
                                onChange={(checked) => {
                                    if (isNotificationToggling) return;

                                    setLocalEmailNotificationEnabled(checked);
                                    setIsNotificationToggling(true);

                                    if (onNotificationChange) {
                                        Promise.resolve(onNotificationChange(checked))
                                            .finally(() => {
                                                setIsNotificationToggling(false);
                                            });
                                    } else {
                                        setIsNotificationToggling(false);
                                    }
                                }}
                            />
                        </div>

                        <JobDigestRoleControl jobId={jobId} token={token} />
                    </Card>
                    </section>

                    <section
                        id={SETTINGS_SECTION.messages}
                        className={sectionClass(SETTINGS_SECTION.messages)}
                        aria-labelledby={`${SETTINGS_SECTION.messages}-heading`}
                    >
                        <div className="w-full">
                            <div
                                id={`${SETTINGS_SECTION.messages}-heading`}
                                className="font-semibold text-gray-600 text-body-lg mb-1"
                            >
                                Message Settings
                            </div>
                            <div className="text-gray-600 text-small mb-4">
                                Customize how June handles conversations to provide the best candidate experience.
                            </div>
                        </div>

                    <Card className="w-full mb-2">
                        <div className="mb-2 text-body-lg">Welcome Message</div>
                        <Textarea
                            className="h-32 mb-1"
                            value={introMessage}
                            onChange={(e) => setIntroMessage(e.target.value)}
                            placeholder="Enter your custom welcome message..."
                        />
                        <div className="text-small text-gray-500">
                            We automatically add the following to the end of every welcome message: "{autoAppendedInviteText()}"
                        </div>
                        <div className="flex items-center" style={{ position: 'relative' }}>
                            <span className="text-gray-600 font-medium text-small pr-2">Variables</span>
                            <Tooltip
                                position="top"
                                content="Click the Variables below to copy and paste them into your message."
                            >
                                <CircleHelp size="1em" />
                            </Tooltip>
                        </div>
                        <div className="mb-2 flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div className="flex w-full flex-wrap gap-2 lg:w-auto">
                                {["{candidate_name}", "{role_name}", "{company_name}"].map(variable => (
                                    <Button
                                        key={variable}
                                        variant="secondary"
                                        onClick={() => copyVariable(variable)}
                                        rightIcon={<Copy size="1em" />}
                                    >
                                        {variable}
                                    </Button>
                                ))}
                            </div>
                            <div className="flex w-full flex-wrap gap-2 lg:w-auto lg:justify-end">
                                <Button
                                    variant="tertiary"
                                    onClick={resetIntroToDefaultHandler}
                                    disabled={introMessage === defaultIntroMessage}
                                >
                                    Reset to Default
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={resetIntroMessageHandler}
                                    disabled={!introMessageChanged}
                                >
                                    Clear Changes
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={saveIntroMessageHandler}
                                    disabled={!introMessageChanged || isLoading}
                                >
                                    Save
                                </Button>
                            </div>
                        </div>
                    </Card>

                    {/* Outro Message Card */}
                    <Card className="w-full">
                        <div className="mb-2 text-body-lg">Outro Message</div>
                        <Textarea
                            className="h-24 mb-1"
                            value={outroMessage}
                            onChange={(e) => setOutroMessage(e.target.value)}
                            placeholder="Enter your custom outro message..."
                        />
                        <div className="text-small text-gray-500">
                            Unless you enter a custom message, the default outro message may vary based on the conversation.
                            The default message below is an example of a typical outro message from June.
                        </div>
                        <div className="flex items-center" style={{ position: 'relative' }}>
                            <span className="text-gray-600 font-medium text-small pr-2">Variables</span>
                            <Tooltip
                                position="top"
                                content="Click the Variables below to copy and paste them into your message."
                            >
                                <CircleHelp size="1em" />
                            </Tooltip>
                        </div>
                        <div className="mb-2 flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div className="flex w-full flex-wrap gap-2 lg:w-auto">
                                {["{candidate_name}", "{role_name}", "{company_name}"].map(variable => (
                                    <Button
                                        key={variable}
                                        variant="secondary"
                                        onClick={() => copyVariable(variable)}
                                        rightIcon={<Copy size="1em" />}
                                    >
                                        {variable}
                                    </Button>
                                ))}
                            </div>
                            <div className="flex w-full flex-wrap gap-2 lg:w-auto lg:justify-end">
                                <Button
                                    variant="tertiary"
                                    onClick={resetOutroToDefaultHandler}
                                    disabled={outroMessage === defaultOutroMessage}
                                >
                                    Reset to Default
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={resetOutroMessageHandler}
                                    disabled={!outroMessageChanged}
                                >
                                    Clear Changes
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={saveOutroMessageHandler}
                                    disabled={!outroMessageChanged || isLoading}
                                >
                                    Save
                                </Button>
                            </div>
                        </div>
                    </Card>

                    {/* Calendar Link Message Card */}
                    <Card className="w-full mb-2">
                        <div className="mb-2 text-body-lg">Calendar Link Message</div>
                        <Textarea
                            className="h-24 mb-1"
                            value={calendarMessage}
                            onChange={(e) => setCalendarMessage(e.target.value)}
                            placeholder="Enter your custom calendar link message..."
                        />
                        <div className="text-small text-gray-500">
                            Customize the text message June sends when sharing your calendar link with applicants.
                        </div>
                        <div className="flex items-center" style={{ position: 'relative' }}>
                            <span className="text-gray-600 font-medium text-small pr-2">Variables</span>
                            <Tooltip
                                position="top"
                                content="Click the Variables below to copy and paste them into your message."
                            >
                                <CircleHelp size="1em" />
                            </Tooltip>
                        </div>
                        <div className="mb-2 flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div className="flex w-full flex-wrap gap-2 lg:w-auto">
                                {["{candidate_name}", "{role_name}", "{company_name}", "{calendar_link}"].map(variable => (
                                    <Button
                                        key={variable}
                                        variant="secondary"
                                        onClick={() => copyVariable(variable)}
                                        rightIcon={<Copy size="1em" />}
                                    >
                                        {variable}
                                    </Button>
                                ))}
                            </div>
                            <div className="flex w-full flex-wrap gap-2 lg:w-auto lg:justify-end">
                                <Button
                                    variant="tertiary"
                                    onClick={resetCalendarMessageToDefaultHandler}
                                    disabled={calendarMessage === defaultCalendarMessage}
                                >
                                    Reset to Default
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={resetCalendarMessageHandler}
                                    disabled={!calendarMessageChanged}
                                >
                                    Clear Changes
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={saveCalendarMessageHandler}
                                    disabled={!calendarMessageChanged || isLoading}
                                >
                                    Save
                                </Button>
                            </div>
                        </div>
                    </Card>

                    {/* Scheduling Reminder Message Card */}
                    <Card className="w-full">
                        <div className="mb-2 text-body-lg">Scheduling Reminder Message</div>
                        <Textarea
                            className="h-24 mb-1"
                            value={calendarReminderMessage}
                            onChange={(e) => setCalendarReminderMessage(e.target.value)}
                            placeholder="Enter your custom scheduling reminder message..."
                        />
                        <div className="text-small text-gray-500">
                            Customize the reminder text message June sends when reminding applicants to schedule a call.
                        </div>
                        <div className="flex items-center" style={{ position: 'relative' }}>
                            <span className="text-gray-600 font-medium text-small pr-2">Variables</span>
                            <Tooltip
                                position="top"
                                content="Click the Variables below to copy and paste them into your message."
                            >
                                <CircleHelp size="1em" />
                            </Tooltip>
                        </div>
                        <div className="mb-2 flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div className="flex w-full flex-wrap gap-2 lg:w-auto">
                                {["{candidate_name}", "{role_name}", "{company_name}", "{calendar_link}"].map(variable => (
                                    <Button
                                        key={variable}
                                        variant="secondary"
                                        onClick={() => copyVariable(variable)}
                                        rightIcon={<Copy size="1em" />}
                                    >
                                        {variable}
                                    </Button>
                                ))}
                            </div>
                            <div className="flex w-full flex-wrap gap-2 lg:w-auto lg:justify-end">
                                <Button
                                    variant="tertiary"
                                    onClick={resetCalendarReminderMessageToDefaultHandler}
                                    disabled={calendarReminderMessage === defaultCalendarReminderMessage}
                                >
                                    Reset to Default
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={resetCalendarReminderMessageHandler}
                                    disabled={!calendarReminderMessageChanged}
                                >
                                    Clear Changes
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={saveCalendarReminderMessageHandler}
                                    disabled={!calendarReminderMessageChanged || isLoading}
                                >
                                    Save
                                </Button>
                            </div>
                        </div>
                    </Card>
                    </section>

                    <section
                        id={SETTINGS_SECTION.atsStages}
                        className={sectionClass(SETTINGS_SECTION.atsStages)}
                        aria-labelledby={`${SETTINGS_SECTION.atsStages}-heading`}
                    >
                        <div
                            id={`${SETTINGS_SECTION.atsStages}-heading`}
                            className="font-semibold text-gray-600 text-body-lg mb-1"
                        >
                            ATS Stages
                        </div>
                        <JobAtsStageSettings
                            jobId={jobId}
                            companyId={companyId}
                            token={token}
                            atsJobId={atsJobId}
                            automaticInterviews={automaticInterviews}
                            interviewEligibleStageNames={interviewEligibleStageNames}
                            autoAdvanceTargetStage={autoAdvanceTargetStage}
                            autoAdvanceHighMatchStage={autoAdvanceHighMatchStage}
                            autoAdvanceMinMatchRating={autoAdvanceMinMatchRating}
                            autoAdvanceHumanInterviewStage={autoAdvanceHumanInterviewStage}
                            onSettingsSaved={onStageSettingsSaved}
                            onAutomaticInterviewsChange={onAutomaticInterviewsChange}
                        />
                    </section>

                    {isDevEnvironment() && (
                        <Card className="w-full border-amber-300 bg-amber-50">
                            <div className="text-body-lg font-medium text-gray-900">Developer Tools</div>
                            <div className="text-small text-gray-600">
                                Refresh this role&apos;s interview snapshots with its current Scoring Rubrics and analysis criteria, replacing legacy desired responses and older rubrics, so analysis can be re-run against them. Questions that changed or do not yet have a rubric are left untouched.
                            </div>
                            <div className="flex justify-end">
                                <Button
                                    variant="danger"
                                    onClick={() => setShowSnapshotMigrationModal(true)}
                                    data-cy="replace-snapshot-desired-responses"
                                >
                                    Refresh Snapshot Rubrics
                                </Button>
                            </div>
                            <div className="text-small text-gray-600">
                                Regenerate the Scoring Rubric for every question on this role from the current role context. Edits to existing rubrics are overwritten.
                            </div>
                            <div className="flex justify-end">
                                <Button
                                    variant="danger"
                                    onClick={() => setShowRubricRegenerationModal(true)}
                                    data-cy="regenerate-scoring-rubrics"
                                >
                                    Regenerate All Scoring Rubrics
                                </Button>
                            </div>
                        </Card>
                    )}
                    </div>
                </div>
            <ConfirmationModal
                isOpen={showSnapshotMigrationModal}
                onClose={() => setShowSnapshotMigrationModal(false)}
                onConfirm={replaceSnapshotDesiredResponses}
                title="Refresh snapshot rubrics?"
                details="This updates historical interview snapshots for this role. Question snapshots get the current Scoring Rubric where the question text is an exact, unambiguous match; legacy desired-response text is removed from them. Every interview gets the current evaluation criteria. Re-run evaluation afterwards to score against them."
                confirmButtonText="Refresh Snapshots"
                confirmButtonVariant="danger"
                isLoading={isMigratingSnapshots}
                loadingText="Refreshing Snapshots..."
                dismissible={!isMigratingSnapshots}
                dataCyConfirm="confirm-replace-snapshot-desired-responses"
            />
            <ConfirmationModal
                isOpen={showRubricRegenerationModal}
                onClose={() => setShowRubricRegenerationModal(false)}
                onConfirm={regenerateScoringRubrics}
                title="Regenerate all Scoring Rubrics?"
                details="Every question's Scoring Rubric is replaced with a newly generated one, including rubrics you have edited. Interviews already sent keep their snapshots until you refresh them."
                confirmButtonText="Regenerate Rubrics"
                confirmButtonVariant="danger"
                isLoading={isRegeneratingRubrics}
                loadingText="Regenerating..."
                dismissible={!isRegeneratingRubrics}
                dataCyConfirm="confirm-regenerate-scoring-rubrics"
            />
        </div>
    );
}
